import React, { useContext, useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Badge,
  Alert,
  Row,
  Col,
  Modal,
  Form,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faTools,
  faSpinner,
  faSync,
  faClock,
  faCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { MaintenanceContext } from "../../../contexts/MaintenanceContext";
import { popup } from "../../common/popup";

const ActiveTab = () => {
  const {
    machines = [],
    handleMarkFixed,
    handleMarkUnderMaintenance,
    loading = false,
    success,
    error,
  } = useContext(MaintenanceContext);

  // trigger re-render durasi
  const [, setTimer] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setTimer(Date.now()), 10000);
    return () => clearInterval(i);
  }, []);

  // ===== Finish maintenance modal =====
  // Tombol "Selesai" dulu memanggil handleMarkFixed(id) TANPA data solusi,
  // sehingga ketahan guard finishMaintenance (butuh solutionData) dan tidak
  // melakukan apa-apa. Sekarang kita kumpulkan laporan solusi dulu.
  const emptyFinishForm = {
    gejalaMasalah: "",
    uraianMasalah: "",
    tindakanPenyebab: "",
    evakuasiPerbaikan: "",
    dikerjakanOleh: "",
    dicekOleh: "",
    keterangan: "",
  };
  const [showFinish, setShowFinish] = useState(false);
  const [finishMachine, setFinishMachine] = useState(null);
  const [finishForm, setFinishForm] = useState(emptyFinishForm);
  const [submitting, setSubmitting] = useState(false);

  const openFinish = (m) => {
    setFinishMachine(m);
    setFinishForm(emptyFinishForm);
    setShowFinish(true);
  };

  const closeFinish = () => {
    if (submitting) return;
    setShowFinish(false);
    setFinishMachine(null);
  };

  const handleFinishField = (e) =>
    setFinishForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const submitFinish = async () => {
    if (
      !finishForm.uraianMasalah ||
      !finishForm.dikerjakanOleh ||
      !finishForm.dicekOleh
    ) {
      popup.error(
        "Uraian masalah, Dikerjakan oleh, dan Dicek oleh wajib diisi.",
      );
      return;
    }
    if (!finishMachine) return;
    try {
      setSubmitting(true);
      const ok = await handleMarkFixed(finishMachine.id, finishForm);
      if (ok) {
        setShowFinish(false);
        setFinishMachine(null);
        setFinishForm(emptyFinishForm);
        popup.success("Perbaikan selesai");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const needMaintenanceMachines = machines.filter(
    (m) => m.status === "Need Maintenance"
  );
  const underMaintenanceMachines = machines.filter(
    (m) => m.status === "Under Maintenance"
  );

  const formatDateTime = (date) =>
    date
      ? new Date(date).toLocaleString("id-ID", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A";

  const calculateDuration = (start) => {
    if (!start) return "N/A";
    const minutes = Math.floor((Date.now() - new Date(start)) / 60000);
    if (minutes < 60) return `${minutes} menit`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}j ${minutes % 60}m`;
    return `${Math.floor(minutes / 1440)} hari`;
  };

  if (loading)
    return (
      <div className="text-center p-5">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
        <h5>Memuat data...</h5>
      </div>
    );

  return (
    <>
      {success && <Alert variant="success">{success}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Header className="d-flex justify-content-between">
          <h5>
            <FontAwesomeIcon icon={faTools} /> Maintenance Aktif
          </h5>
          <Badge bg="warning">
            {needMaintenanceMachines.length + underMaintenanceMachines.length}{" "}
            Mesin
          </Badge>
        </Card.Header>

        <Card.Body className="p-0">
          <Table responsive hover>
            <thead>
              <tr>
                <th>KODE</th>
                <th>NAMA MESIN</th>
                <th>SEKTOR</th>
                <th>MULAI PERBAIKAN</th>
                <th>DURASI</th>
                <th>TINDAKAN</th>
              </tr>
            </thead>
            <tbody>
              {/* NEED MAINTENANCE */}
              {needMaintenanceMachines.map((m) => (
                <tr key={m.id}>
                  <td>
                    <code>{m.code}</code>
                  </td>
                  <td>{m.name}</td>
                  <td>
                    <Badge bg="info">{m.sector?.name || "N/A"}</Badge>
                  </td>
                  <td>N/A</td>
                  <td>-</td>
                  <td>
                    <Button
                      size="sm"
                      onClick={() => handleMarkUnderMaintenance(m.id)}
                    >
                      <FontAwesomeIcon icon={faTools} /> Kerjakan
                    </Button>
                  </td>
                </tr>
              ))}

              {/* UNDER MAINTENANCE */}
              {underMaintenanceMachines.map((m) => (
                <tr key={m.id}>
                  <td>
                    <code>{m.code}</code>
                  </td>
                  <td>{m.name}</td>
                  <td>
                    <Badge bg="info">{m.sector?.name || "N/A"}</Badge>
                  </td>
                  <td>{formatDateTime(m.maintenance_start_time)}</td>
                  <td>
                    <Badge bg="danger">
                      {calculateDuration(m.maintenance_start_time)}
                    </Badge>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => openFinish(m)}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} /> Selesai
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Modal laporan solusi sebelum menyelesaikan maintenance */}
      <Modal show={showFinish} onHide={closeFinish} size="lg" backdrop="static">
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>
            Selesaikan Maintenance{" "}
            {finishMachine ? `— ${finishMachine.name} (${finishMachine.code})` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Dikerjakan Oleh <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="dikerjakanOleh"
                    value={finishForm.dikerjakanOleh}
                    onChange={handleFinishField}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Dicek Oleh <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="dicekOleh"
                    value={finishForm.dicekOleh}
                    onChange={handleFinishField}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Gejala Masalah</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="gejalaMasalah"
                value={finishForm.gejalaMasalah}
                onChange={handleFinishField}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Uraian Masalah <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="uraianMasalah"
                value={finishForm.uraianMasalah}
                onChange={handleFinishField}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tindakan / Penyebab</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="tindakanPenyebab"
                value={finishForm.tindakanPenyebab}
                onChange={handleFinishField}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Evakuasi / Perbaikan</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="evakuasiPerbaikan"
                value={finishForm.evakuasiPerbaikan}
                onChange={handleFinishField}
              />
            </Form.Group>

            <Form.Group className="mb-1">
              <Form.Label>Keterangan</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="keterangan"
                value={finishForm.keterangan}
                onChange={handleFinishField}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeFinish} disabled={submitting}>
            Batal
          </Button>
          <Button variant="success" onClick={submitFinish} disabled={submitting}>
            <FontAwesomeIcon icon={submitting ? faSpinner : faCheckCircle} spin={submitting} />{" "}
            {submitting ? "Menyimpan..." : "Selesaikan"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ActiveTab;
